/* CinematicWalkthroughSection — pick a historical event, upload your photo,
 * walk through the event as a sequence of scene-images with narration.
 * Optional Sora 2 video at climax (generated in background). */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Film, Camera, X, Sparkles, Loader2, ChevronLeft, ChevronRight, Trash2,
  Play, Pause, Volume2, VolumeX, Clock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function compressImage(file, maxSize = 800) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          if (width > height) { height = (height * maxSize) / width; width = maxSize; }
          else { width = (width * maxSize) / height; height = maxSize; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ============== PLAYER ==============
function CinematicPlayer({ walkthrough, onUpdated, onClose }) {
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const [polling, setPolling] = useState(
    !!walkthrough.climax_video && ['queued', 'generating'].includes(walkthrough.climax_video?.status)
    || Object.values(walkthrough.scene_videos || {}).some((v) => ['queued', 'generating'].includes(v?.status))
  );
  const utterRef = useRef(null);
  const pollRef = useRef(null);
  const currentWid = walkthrough.id;

  const scene = walkthrough.scenes[idx];
  const isLast = idx === walkthrough.scenes.length - 1;
  const isClimax = idx === 2; // 3rd scene is the climax
  const climaxVideo = walkthrough.climax_video;
  // NEW: per-scene video in hybrid mode (scenes 1, 2, 3)
  const sceneVideo = walkthrough.scene_videos?.[String(idx)];
  const sceneVideoReady = sceneVideo?.status === 'ready' && sceneVideo?.video_base64;
  const sceneVideoLoading = sceneVideo && ['queued', 'generating'].includes(sceneVideo.status);

  const speak = useCallback((text) => {
    if (muted || !window.speechSynthesis || !text) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    const sv = voices.find((v) => v.lang.startsWith('sv'));
    if (sv) { u.voice = sv; u.lang = sv.lang; }
    u.rate = 0.95;
    utterRef.current = u;
    window.speechSynthesis.speak(u);
  }, [muted]);

  // Auto-narrate on scene change
  useEffect(() => {
    if (!autoPlay) return;
    speak(scene?.narration);
    return () => { try { window.speechSynthesis?.cancel(); } catch { /* */ } };
  }, [idx, autoPlay, speak, scene]);

  // Poll for video status if anything is generating
  useEffect(() => {
    if (!polling) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/timetravel/cinematic/${currentWid}`, { withCredentials: true });
        const w = data?.walkthrough;
        if (!w) return;
        const cv = w.climax_video;
        const sv = w.scene_videos || {};
        const stillGoing =
          (cv && ['queued', 'generating'].includes(cv.status)) ||
          Object.values(sv).some((v) => ['queued', 'generating'].includes(v?.status));
        onUpdated?.(w);
        if (!stillGoing) {
          clearInterval(pollRef.current);
          setPolling(false);
          const anyReady = (cv?.status === 'ready') || Object.values(sv).some((v) => v?.status === 'ready');
          if (anyReady) toast.success('🎬 Videoklipp klara!');
        }
      } catch { /* */ }
    }, 8000);
    return () => clearInterval(pollRef.current);
  }, [polling, currentWid, onUpdated]);

  const next = () => { if (!isLast) setIdx((i) => i + 1); };
  const prev = () => { if (idx > 0) setIdx((i) => i - 1); };
  const close = () => {
    try { window.speechSynthesis?.cancel(); } catch { /* */ }
    onClose();
  };

  if (!scene) return null;

  return (
    <div className="fixed inset-0 z-[130] bg-black flex flex-col" data-testid="cinematic-player">
      {/* Top bar */}
      <header className="flex items-center justify-between p-3 text-white shrink-0 bg-gradient-to-b from-black/80 to-transparent absolute inset-x-0 top-0 z-10">
        <div className="flex items-center gap-2">
          <Film className="w-4 h-4" />
          <div>
            <p className="text-[10px] uppercase opacity-60 tracking-wide">{walkthrough.era}</p>
            <h3 className="font-['Fredoka'] font-bold text-sm">{walkthrough.emoji} {walkthrough.event_title}</h3>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMuted((m) => !m)}
            className="bg-white/15 hover:bg-white/25 rounded-full p-2"
            data-testid="player-mute">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <button onClick={() => setAutoPlay((a) => !a)}
            className="bg-white/15 hover:bg-white/25 rounded-full p-2"
            data-testid="player-autoplay">
            {autoPlay ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button onClick={close}
            className="bg-white/15 hover:bg-white/25 rounded-full p-2"
            data-testid="player-close">
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Scene image OR video */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        {sceneVideoReady ? (
          <video
            src={`data:${sceneVideo.mime_type || 'video/mp4'};base64,${sceneVideo.video_base64}`}
            controls autoPlay loop
            className="max-w-full max-h-full"
            data-testid={`player-scene-video-${idx}`}
          />
        ) : isClimax && climaxVideo?.status === 'ready' && climaxVideo.video_base64 ? (
          <video
            src={`data:${climaxVideo.mime_type || 'video/mp4'};base64,${climaxVideo.video_base64}`}
            controls autoPlay loop
            className="max-w-full max-h-full"
            data-testid="player-climax-video"
          />
        ) : scene.image_base64 ? (
          <img
            src={`data:${scene.mime_type || 'image/png'};base64,${scene.image_base64}`}
            alt={scene.label}
            className="max-w-full max-h-full object-contain"
            data-testid="player-scene-image"
          />
        ) : (
          <div className="text-white text-center px-6" data-testid="player-scene-error">
            <p className="text-3xl">😢</p>
            <p className="opacity-70 mt-2">Den här scenen kunde inte genereras</p>
            {scene.error && <p className="text-xs opacity-50 mt-1">{scene.error}</p>}
          </div>
        )}

        {/* Scene video status banner */}
        {sceneVideoLoading && (
          <div className="absolute top-20 left-1/2 -translate-x-1/2 neo-card px-3 py-2 bg-black/70 text-white text-xs flex items-center gap-2" data-testid={`player-video-status-${idx}`}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Skapar video för denna scen... (2-5 min)
          </div>
        )}

        {/* Legacy climax video status */}
        {!sceneVideo && isClimax && climaxVideo && climaxVideo.status !== 'ready' && (
          <div className="absolute top-20 left-1/2 -translate-x-1/2 neo-card px-3 py-2 bg-black/70 text-white text-xs flex items-center gap-2" data-testid="player-video-status">
            {['queued', 'generating'].includes(climaxVideo.status) ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Skapar klimax-video... (2-5 min)</>
            ) : (
              <>⚠️ Video-generering misslyckades</>
            )}
          </div>
        )}
      </div>

      {/* Narration overlay */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent p-4 sm:p-6 pb-20 text-white">
        <p className="text-[10px] uppercase tracking-wide opacity-60 font-bold mb-1">
          Scen {idx + 1} av {walkthrough.scenes.length} · {scene.label}
        </p>
        <p className="text-base sm:text-lg leading-relaxed font-['Fredoka'] max-w-3xl" data-testid="player-narration">
          {scene.narration}
        </p>
      </div>

      {/* Bottom controls */}
      <footer className="absolute inset-x-0 bottom-0 p-3 flex items-center gap-2 z-10">
        <button onClick={prev} disabled={idx === 0}
          className="neo-btn px-3 py-2 bg-white/20 backdrop-blur text-white text-sm font-bold disabled:opacity-30"
          data-testid="player-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 flex gap-1">
          {walkthrough.scenes.map((_, i) => (
            <button key={i}
              onClick={() => setIdx(i)}
              className={`flex-1 h-1.5 rounded-full transition ${i === idx ? 'bg-white' : i < idx ? 'bg-white/60' : 'bg-white/20'}`}
              data-testid={`player-dot-${i}`}
            />
          ))}
        </div>
        <button onClick={next} disabled={isLast}
          className="neo-btn px-3 py-2 bg-white/20 backdrop-blur text-white text-sm font-bold disabled:opacity-30"
          data-testid="player-next">
          <ChevronRight className="w-4 h-4" />
        </button>
      </footer>
    </div>
  );
}

// ============== MAIN ==============
export default function CinematicWalkthroughSection() {
  const [events, setEvents] = useState([]);
  const [walkthroughs, setWalkthroughs] = useState([]);
  const [eventKey, setEventKey] = useState('');
  const [photo, setPhoto] = useState(null);
  const [videoMode, setVideoMode] = useState('hybrid'); // 'none' | 'climax' | 'hybrid'
  const [creating, setCreating] = useState(false);
  const [openWalkthrough, setOpenWalkthrough] = useState(null);
  const fileRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [evRes, wRes] = await Promise.all([
        axios.get(`${API}/timetravel/cinematic/events`, { withCredentials: true }),
        axios.get(`${API}/timetravel/cinematic/list`, { withCredentials: true }),
      ]);
      setEvents(evRes.data?.events || []);
      setWalkthroughs(wRes.data?.items || []);
    } catch { /* */ }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { setPhoto(await compressImage(file)); }
    catch { toast.error('Kunde inte läsa bilden'); }
  };

  const startWalkthrough = async () => {
    if (!photo) { toast.error('Ladda upp en bild på dig först 📸'); return; }
    if (!eventKey) { toast.error('Välj en händelse'); return; }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/timetravel/cinematic/start`, {
        event_key: eventKey,
        selfie_base64: photo,
        hybrid_videos: videoMode === 'hybrid',
        include_climax_video: videoMode === 'climax',
      }, { withCredentials: true, timeout: 180_000 });
      toast.success('Filmupplevelse skapad! 🎬');
      // Open full walkthrough for the player
      const { data: full } = await axios.get(`${API}/timetravel/cinematic/${data.walkthrough.id}`, { withCredentials: true });
      setOpenWalkthrough(full.walkthrough);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte skapa filmupplevelse');
    } finally { setCreating(false); }
  };

  const openExisting = async (id) => {
    try {
      const { data } = await axios.get(`${API}/timetravel/cinematic/${id}`, { withCredentials: true });
      setOpenWalkthrough(data.walkthrough);
    } catch { toast.error('Kunde inte öppna'); }
  };

  const deleteWalkthrough = async (id) => {
    if (!window.confirm('Ta bort denna filmupplevelse?')) return;
    try {
      await axios.delete(`${API}/timetravel/cinematic/${id}`, { withCredentials: true });
      loadAll();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const onPlayerUpdated = (updated) => {
    setOpenWalkthrough(updated);
    loadAll();
  };

  return (
    <div className="neo-card p-4 bg-gradient-to-br from-[#1a1a2e]/5 via-[#FFE8F5] to-[#FFFAF0]" data-testid="cinematic-section">
      <div className="flex items-center gap-2 mb-2">
        <Film className="w-4 h-4 text-[#D06892]" />
        <h3 className="font-['Fredoka'] font-bold text-base">Filmupplevelse — gå runt i historien 🎬</h3>
      </div>
      <p className="text-xs opacity-70 mb-3">
        Välj en händelse + ladda upp en bild → 5 scener med dig i, röst-berättelse på svenska, valfri klimax-video (Sora 2). Som en interaktiv film med dig själv i 🌟
      </p>

      {/* Photo upload */}
      <div className="mb-3">
        <p className="text-[10px] font-bold uppercase opacity-60 mb-1">1. Din bild</p>
        {photo ? (
          <div className="relative inline-block">
            <img src={photo} alt="Din bild" className="w-32 h-32 object-cover rounded-lg border-2 border-black/10" />
            <button onClick={() => setPhoto(null)} className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow" data-testid="cin-photo-remove">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()}
            className="w-full sm:w-auto neo-btn px-4 py-3 bg-white border-dashed text-sm flex items-center gap-2"
            data-testid="cin-photo-upload">
            <Camera className="w-4 h-4" /> Välj bild från ditt galleri
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" onChange={pickPhoto} className="hidden" />
      </div>

      {/* Event picker */}
      <div className="mb-3">
        <p className="text-[10px] font-bold uppercase opacity-60 mb-1">2. Välj händelse</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="cin-events">
          {events.map((ev) => (
            <button key={ev.key}
              onClick={() => setEventKey(ev.key)}
              className={`text-left p-2 rounded-lg border-2 transition ${eventKey === ev.key ? 'bg-[#FFE8F5] border-[#D06892] ring-2 ring-[#D06892]/30' : 'bg-white border-black/10 hover:border-[#D06892]/40'}`}
              data-testid={`cin-event-${ev.key}`}>
              <div className="flex items-center gap-2">
                <span className="text-2xl shrink-0">{ev.emoji}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm truncate">{ev.title}</p>
                  <p className="text-[10px] opacity-60">{ev.era}</p>
                </div>
              </div>
              <p className="text-[10px] opacity-70 mt-1 line-clamp-2">{ev.summary}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Video option */}
      <div className="mb-3" data-testid="cin-video-mode">
        <p className="text-[10px] font-bold uppercase opacity-60 mb-1">3. Hur mycket film?</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1">
          <button onClick={() => setVideoMode('none')}
            className={`text-left p-2 rounded-lg border-2 text-xs ${videoMode === 'none' ? 'bg-[#FFE8F5] border-[#D06892] ring-2 ring-[#D06892]/30' : 'bg-white border-black/10'}`}
            data-testid="video-mode-none">
            <p className="font-bold">📸 Endast bilder</p>
            <p className="text-[10px] opacity-60 mt-0.5">5 scener · ~1-2 min totalt</p>
          </button>
          <button onClick={() => setVideoMode('climax')}
            className={`text-left p-2 rounded-lg border-2 text-xs ${videoMode === 'climax' ? 'bg-[#FFE8F5] border-[#D06892] ring-2 ring-[#D06892]/30' : 'bg-white border-black/10'}`}
            data-testid="video-mode-climax">
            <p className="font-bold">🎬 Klimax-video</p>
            <p className="text-[10px] opacity-60 mt-0.5">+ 1 video · ~3-7 min totalt</p>
          </button>
          <button onClick={() => setVideoMode('hybrid')}
            className={`text-left p-2 rounded-lg border-2 text-xs ${videoMode === 'hybrid' ? 'bg-[#FFE8F5] border-[#D06892] ring-2 ring-[#D06892]/30' : 'bg-white border-black/10'}`}
            data-testid="video-mode-hybrid">
            <p className="font-bold">🌟 Hybrid (rek.)</p>
            <p className="text-[10px] opacity-60 mt-0.5">3 videor + 2 bilder · ~6-12 min</p>
          </button>
        </div>
        <p className="text-[10px] opacity-60 italic mt-1">
          {videoMode === 'hybrid' && 'Video på möte, klimax och efterspel. Bilder på ankomst och reflektion.'}
          {videoMode === 'climax' && 'En video på huvudscenen, bilder på resten.'}
          {videoMode === 'none' && 'Bara stillbilder — snabbast.'}
        </p>
      </div>

      <button onClick={startWalkthrough}
        disabled={creating || !photo || !eventKey}
        className="neo-btn w-full py-3 bg-[#FFC8DD] font-bold text-base flex items-center justify-center gap-2"
        data-testid="cin-start">
        {creating
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Skapar filmupplevelse... (1-2 min)</>
          : <><Sparkles className="w-4 h-4" /> Starta filmupplevelsen ✨</>}
      </button>

      {/* Past walkthroughs */}
      {walkthroughs.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] font-bold uppercase opacity-60 mb-2">Dina filmupplevelser ({walkthroughs.length})</p>
          <div className="space-y-2" data-testid="cin-walkthroughs-list">
            {walkthroughs.map((w) => (
              <div key={w.id} className="neo-card p-2 bg-white flex items-center gap-2" data-testid={`cin-wt-${w.id}`}>
                <span className="text-2xl">{w.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{w.event_title}</p>
                  <p className="text-[10px] opacity-60">
                    {w.era} · {w.scenes?.length || 0} scener
                    {(() => {
                      const sv = w.scene_videos || {};
                      const total = Object.keys(sv).length;
                      const ready = Object.values(sv).filter((v) => v?.status === 'ready').length;
                      const inProg = Object.values(sv).filter((v) => ['queued', 'generating'].includes(v?.status)).length;
                      if (total > 0) {
                        if (ready === total) return ' · 🎬 Alla videor klara';
                        if (inProg > 0) return ` · ⏳ ${ready}/${total} videor klara`;
                        return ` · 🎬 ${ready}/${total} videor`;
                      }
                      if (w.climax_video?.status === 'ready') return ' · 🎬 Video klar';
                      if (['queued', 'generating'].includes(w.climax_video?.status)) return ' · ⏳ Video skapas';
                      return '';
                    })()}
                  </p>
                </div>
                <button onClick={() => openExisting(w.id)}
                  className="neo-btn px-2 py-1 bg-[#B9FBC0] text-xs font-bold"
                  data-testid={`cin-wt-play-${w.id}`}>
                  <Play className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => deleteWalkthrough(w.id)}
                  className="neo-btn p-1.5 bg-white text-red-600"
                  data-testid={`cin-wt-delete-${w.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {openWalkthrough && (
        <CinematicPlayer
          walkthrough={openWalkthrough}
          onUpdated={onPlayerUpdated}
          onClose={() => setOpenWalkthrough(null)}
        />
      )}
    </div>
  );
}
